import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Eye, ChevronLeft, ChevronRight, Clock, Award,
  TrendingUp, DollarSign, Shield, Loader2, Inbox
} from 'lucide-react';
import { submissionsAPI } from '../services/api';

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700',
};

const GRADE_COLORS = {
  S: 'bg-purple-100 text-purple-700 border-purple-300',
  A: 'bg-green-100 text-green-700 border-green-300',
  B: 'bg-blue-100 text-blue-700 border-blue-300',
  C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  D: 'bg-orange-100 text-orange-700 border-orange-300',
  F: 'bg-red-100 text-red-700 border-red-300',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'beginner', label: 'Beginner' },
  { key: 'intermediate', label: 'Intermediate' },
  { key: 'advanced', label: 'Advanced' },
];

const formatTime = (seconds) => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

const GradeBadge = ({ grade }) => {
  if (!grade) return <span className="text-slate-500">-</span>;
  const colors = GRADE_COLORS[grade] || GRADE_COLORS.F;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${colors}`}>
      {grade}
    </span>
  );
};

const DifficultyBadge = ({ difficulty }) => {
  const colors = DIFFICULTY_COLORS[difficulty] || DIFFICULTY_COLORS.beginner;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors}`}>
      {difficulty}
    </span>
  );
};

const MySubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchSubmissions();
  }, [filter, page]);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * pageSize;
      const response = await submissionsAPI.getHistory(pageSize, offset, filter);
      setSubmissions(response.data.submissions || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8 text-blue-400" />
          My Submissions
        </h1>
        <p className="text-slate-400">
          View all your past submissions across all challenges
        </p>
      </div>

      {/* Filters and Stats */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Filter Tabs */}
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Total Count */}
          <div className="text-slate-400">
            <span className="font-semibold text-white">{total}</span> submissions
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-400">{error}</div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No submissions yet</p>
            <p className="text-slate-500 text-sm mt-1">
              Complete challenges to see your submissions here
            </p>
            <Link
              to="/challenges"
              className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Challenges
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-800/80">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Challenge
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    <Clock className="w-4 h-4 inline" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission, idx) => (
                  <tr
                    key={submission.id}
                    className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${
                      idx % 2 === 0 ? 'bg-slate-800/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/challenge/${submission.challenge_id}`}
                        className="text-white font-medium hover:text-blue-400 transition-colors"
                      >
                        {submission.challenge_title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <DifficultyBadge difficulty={submission.difficulty} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-white font-semibold">
                        {submission.final_score}
                      </span>
                      <span className="text-slate-500 text-sm">/1000</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <GradeBadge grade={submission.grade} />
                    </td>
                    <td className="px-4 py-3 text-center text-slate-400 text-sm">
                      {formatTime(submission.submission_time_seconds)}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">
                      {formatDate(submission.submitted_at?.Time || submission.submitted_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        to={`/challenge/${submission.challenge_id}?submission=${submission.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-lg text-sm transition-colors"
                        title="View this submission"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700 bg-slate-800/80">
            <div className="text-sm text-slate-400">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-slate-300">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Score Legend */}
      {submissions.length > 0 && (
        <div className="mt-6 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-400 mb-3">Grade Scale</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(GRADE_COLORS).map(([grade, _]) => (
              <div key={grade} className="flex items-center gap-2">
                <GradeBadge grade={grade} />
                <span className="text-xs text-slate-500">
                  {grade === 'S' && '950+'}
                  {grade === 'A' && '850-949'}
                  {grade === 'B' && '700-849'}
                  {grade === 'C' && '550-699'}
                  {grade === 'D' && '400-549'}
                  {grade === 'F' && '<400'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubmissionsPage;
